import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Navigation2, ArrowRight, Car, Clock } from 'lucide-react';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { nearbyAreas } from '@/data/seoLocal';

const EASE = [0.22, 1, 0.36, 1];

// Manual regrouping by "secteur" to improve scannability and SEO clustering
const SECTORS = [
  {
    title: 'Vallée du Gier',
    subtitle: 'À 15 minutes maximum',
    keys: ['lhorme', 'rive-de-gier', 'la-grand-croix', 'lorette', 'saint-paul-en-jarez', 'cellieu', 'valfleury', 'saint-christo-en-jarez'],
  },
  {
    title: 'Saint-Étienne & couronne',
    subtitle: 'De 12 à 20 km',
    keys: ['saint-etienne', 'la-talaudiere', 'sorbiers', 'saint-jean-bonnefonds', 'andrezieux-boutheon'],
  },
  {
    title: 'Sud Loire / Ondaine',
    subtitle: 'De 15 à 25 minutes',
    keys: ['firminy', 'unieux'],
  },
];

export default function ZonesPage() {
  return (
    <div className="min-h-screen">
      <PageMeta
        title="Zones desservies · Où nous trouver"
        description="CrossFit Saint-Chamond accueille les sportifs de toute la Loire : Saint-Étienne, Vallée du Gier, Firminy, Unieux, L'Horme, Rive-de-Gier... Trouve ta salle de CrossFit la plus proche."
        path="/zones-desservies"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Zones desservies' }]} />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/zones-desservies"
        eyebrow="Proximité & accessibilité"
        title="Où nous trouver"
        highlight="trouver"
        subtitle="CrossFit Saint-Chamond accueille les membres de plus de 15 communes de la Loire. Notre box est idéalement située au centre de la Vallée du Gier, à 12 min de Saint-Étienne."
        image="https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=1600&q=80"
        alt="Zones desservies par CrossFit Saint-Chamond en Loire (42)"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Zones desservies' }]}
        minHeight="min-h-[55vh]"
      />

      {/* Intro strip */}
      <section className="bg-background py-14" data-testid="zones-intro">
        <div className="container-custom max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="grid sm:grid-cols-3 gap-4"
          >
            {[
              { icon: MapPin, label: '7 Rue Jean-Jacques Rousseau', sub: '42400 Saint-Chamond' },
              { icon: Car, label: 'Parking gratuit à proximité', sub: 'Accès direct depuis l\'A47' },
              { icon: Clock, label: 'Ouvert 6j/7', sub: 'Lun-Ven 9h-20h30 · Sam 9h30-11h30' },
            ].map((it, i) => (
              <Card key={i} className="p-5 border-border/60">
                <it.icon className="h-5 w-5 text-primary mb-2" />
                <div className="font-heading text-sm text-dark tracking-wide">{it.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{it.sub}</div>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-white py-16" data-testid="zones-sectors">
        <div className="container-custom max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="text-center mb-12 max-w-2xl mx-auto"
          >
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Secteurs</span>
            <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3 mb-4">
              Toute la <span className="text-gradient-primary italic font-display">Loire</span> est à 20 min
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Explore les villes autour de la box et découvre le temps de trajet. Chaque ville dispose d'une page dédiée avec itinéraire et transport.
            </p>
          </motion.div>

          <div className="space-y-16">
            {SECTORS.map((sector, sIdx) => {
              const cities = sector.keys
                .map((k) => nearbyAreas.find((a) => a.slug === k))
                .filter(Boolean);
              return (
                <motion.div
                  key={sector.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.8, delay: sIdx * 0.1, ease: EASE }}
                >
                  <div className="flex items-baseline gap-3 mb-6 border-b border-border pb-3">
                    <h3 className="font-heading text-xl md:text-2xl text-dark tracking-wide">
                      {sector.title}
                    </h3>
                    <span className="text-xs text-muted-foreground">{sector.subtitle}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cities.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/crossfit/${c.slug}`}
                        data-testid={`zone-link-${c.slug}`}
                        className="group"
                      >
                        <Card className="p-5 h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/40">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="text-xs text-primary font-semibold tracking-wider uppercase mb-1">
                                CrossFit près de
                              </div>
                              <div className="font-heading text-lg text-dark group-hover:text-primary transition-colors">
                                {c.city}
                              </div>
                            </div>
                            <span className="text-xs bg-accent text-primary font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                              {c.distance}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Navigation2 className="h-3 w-3" /> {c.code}
                          </div>
                          <div className="mt-4 flex items-center justify-end text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                            Voir la page <ArrowRight className="h-3 w-3 ml-1" />
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-dark py-16" data-testid="zones-cta">
        <div className="container-custom text-center max-w-3xl">
          <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-4">
            Ta ville n'est pas listée ?{' '}
            <span className="text-gradient-primary italic font-display">Contacte-nous</span>
          </h2>
          <p className="text-white/70 leading-relaxed mb-8">
            Que tu viennes de Lyon, de la Drôme ou d'ailleurs — si tu es de passage à Saint-Étienne ou dans la Loire,
            tu es le bienvenu pour un Drop-in. Écris-nous, on te répond vite.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="zones-cta-contact">
              <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg">
                ESSAI GRATUIT
              </Button>
            </Link>
            <Link to="/planning" data-testid="zones-cta-planning">
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 hover:text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg bg-transparent"
              >
                VOIR LE PLANNING
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
